import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Custom
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';

import dataController from '@/lib/dataController';
import IzletOpis from '@/models/izlet_opis';
import IzletImages from '@/models/izlet_images';
import { DCFile, DCRecord } from '@/@types/lib/dataController';
import DialogContext from '@/ui/DialogContext/DialogContext';
import IzletiOpisDialog from '../Dialogs/IzletiOpisDialog';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import { Grid } from '@mui/material';
import TableHeading from '@/ui/Table/TableHeading';
import FormContent from '@/components/FormContent';
import { FormContentMode } from '@/@types/components/formContent';
import { sifrarnici } from '@/lib/sifrarnici';
import { ConfirmResult } from '@/@types/lib/confirmDialog';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';
import InfoBox from '@/ui/InfoBox';

type IIzletOpisPaneProps = {
  variant: 'reg' | 'hps';
  mode: string;
  izletId: number;
  onHasOpisPublishUvjet?: (missingIDs: number[]) => void;
};

const IzletOpisPane = (props: IIzletOpisPaneProps) => {
  const { t } = useTranslation();

  const dialogContext = useContext(DialogContext);
  const loaderContext = useContext(LoaderContext);
  const snackbarContext = useContext(SnackbarContext);

  const { variant, mode, izletId, onHasOpisPublishUvjet } = props;

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [izletImage, setIzletImage] = useState<DCFile[]>([]);

  const dcIzletOpis = new dataController(
    IzletOpis,
    IzletOpis.apiPath.replace('{id}', izletId.toString()).replace('hps', variant === 'hps' ? 'hps' : 'reg')
  );
  const dcIzletImages = new dataController(
    IzletImages,
    IzletImages.apiPath.replace('{id}', izletId.toString()).replace('hps', variant === 'hps' ? 'hps' : 'reg')
  );

  const confirmDelete = (): Promise<ConfirmResult> => {
    const title = t('cdialogs.are_you_sure_delete_opis');
    const text = '';
    const confirmButtonText = t('cdialogs.yes_delete');
    const cancelButtonText = t('cdialogs.no_cancel');

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText });
  };

  const title = 'titles.izlet_opis';

  useEffect(() => {
    refreshRecords();
    fetchIzletImage();
  }, []);

  const fetchIzletImage = () => {
    dcIzletImages.GetData().then((resp) => {
      if (resp.success) {
        if (resp.data) {
          setIzletImage((resp.data as DCRecord).image as DCFile[]);
        } else {
          setIzletImage([]);
        }
      } else {
        // No content
        setIzletImage([]);
      }
    });
  };

  const postIzletiImage = (files: DCFile[]) => {
    dcIzletImages.InsertRecord({ image: files }, dcIzletImages.apiPath).then((resp) => {
      if (resp.success) {
        setIzletImage(files);
        const { status } = resp.data as DCRecord;
        snackbarContext.showNotification('notifications.izleti.images.' + status, 'success');
      } else {
        snackbarContext.showNotification('error', 'error');
      }
    });
  };

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dcIzletOpis
      .GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (resp.data.length === 0) {
            // snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  useEffect(() => {
    if (dialogContext.component1) {
      dialogContext.updateDialogProps(2, { disablePickerItems: { poglavlje_id: records.map((r) => r.poglavlje_id) } });
    }
  }, [records]);

  const handleAdd = () => {
    dialogContext.showDialog(
      IzletiOpisDialog,
      {
        dc: dcIzletOpis,
        mode: 'insert',
        form: 'default',
        onClose: handleDialogClose,
        disablePickerItems: { poglavlje_id: records.map((r) => r.poglavlje_id) },
      },
      2
    );
  };

  const handleDialogClose = (result: { dataChanged: boolean; action: string; selId?: number }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];
    dialogContext.showDialog(
      IzletiOpisDialog,
      {
        dc: dcIzletOpis,
        mode: 'update',
        form: 'edit',
        onClose: handleDialogClose,
        recordId: record.id,
        disablePickerItems: { poglavlje_id: records.map((r) => r.poglavlje_id) },
      },
      2
    );
  };

  const allowCRUD = mode !== 'view';

  const handleImgFieldChange = (value: DCFile[], source: string) => {
    postIzletiImage(value);
  };

  useEffect(() => {
    const presentIds = new Set(records.map((rec) => rec.poglavlje_id)); // set cuz rn we can have multiple opis with same poglavlje.. jbg
    const missingRequired = sifrarnici.obavezna_poglavlja_izleta.filter((id) => !presentIds.has(id));

    if (onHasOpisPublishUvjet) {
      onHasOpisPublishUvjet(missingRequired);
    }
  }, [records]);

  const handleDelete = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    confirmDelete().then((result) => {
      if (!result.canceled && result.confirmed) {
        dcIzletOpis.DeleteRecord(record.id as number, dcIzletOpis.apiPath).then((resp) => {
          if (resp.success) {
            snackbarContext.showNotification(t('notifications.delete'), 'success');
            refreshRecords();
          } else {
            const msg = t('error.delete') + resp.error;
            snackbarContext.showNotification(msg, 'error');
          }
        });
      }
    });
  };

  return (
    <Grid container>
      <Grid size={12}>
        <InfoBox labelToken='messages.objava_izleta_helper_1' sx={{ mb: 1}} />
        <InfoBox labelToken='messages.objava_izleta_helper_2' sx={{ mb: 1}} />
        <InfoBox labelToken='messages.objava_izleta_helper_3' sx={{ mb: 1}} />
      </Grid>
      <Grid size={12}>
        <ModelTable
          title={t(title)}
          records={records}
          dc={dcIzletOpis}
          viewName={variant}
          allowSelection="one"
          allowFilter={true}
          allowExport={false}
          allowAdd={allowCRUD}
          handleAdd={handleAdd}
          addLabel={t('buttons.new_opis')}
          allowRowAction={() => {
            return true;
          }}
          onRowAction={() => {}}
        >
          {allowCRUD ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
          {allowCRUD ? <TableButtonEdit variant="outlined" onClick={handleEdit} /> : null}
        </ModelTable>
      </Grid>
      <Grid container size={12} px={3}>
        <Grid size={12}>
          <TableHeading sx={{ mx: 0 }}>{t('titles.izlet_images')}</TableHeading>
        </Grid>
        <FormContent
          mode={mode as FormContentMode}
          fieldNames={dcIzletImages.getFormFieldsNames('default')}
          fields={dcIzletImages.getFormFields('default')}
          record={{ image: izletImage }}
          onFieldChange={handleImgFieldChange}
        />
      </Grid>
      {allowCRUD ? <Grid size={12} px={3}>
        <InfoBox labelToken='messages.izlet_opis_image' />
      </Grid> : null }
    </Grid>
  );
};

export default IzletOpisPane;
